import { useEffect, useState } from 'react';
import { Bookmark, ChevronDown, Check, Trash2, Save, Users, List as ListIcon } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { OperationsViewState, SavedView } from './savedViews';

interface SavedViewPickerProps {
  views: SavedView[];
  activeViewId: string | null;
  currentState: OperationsViewState;
  currentUserId?: string;
  dirty: boolean;
  onSelect: (id: string) => void;
  onCreate: (name: string, isShared: boolean) => void;
  onUpdate: (id: string) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}

export function SavedViewPicker({
  views,
  activeViewId,
  currentUserId,
  dirty,
  onSelect,
  onCreate,
  onUpdate,
  onDelete,
  onClear,
}: SavedViewPickerProps) {
  const [open, setOpen] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShared, setNewShared] = useState(false);

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const displayName = activeView ? activeView.name : 'Default View';
  const isOwner = activeView ? activeView.ownerId === currentUserId : true;

  const myViews = views.filter((v) => v.ownerId === currentUserId);
  const sharedViews = views
    .filter((v) => v.ownerId !== currentUserId && v.isShared)
    .sort((a, b) => a.ownerEmail.localeCompare(b.ownerEmail) || a.name.localeCompare(b.name));

  useEffect(() => {
    if (saveDialogOpen) {
      setNewName(activeView ? `${activeView.name} copy` : '');
      setNewShared(false);
    }
  }, [saveDialogOpen, activeView]);

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    onCreate(name, newShared);
    setNewName('');
    setNewShared(false);
    setSaveDialogOpen(false);
  };

  return (
    <div className="flex items-center gap-0.5 h-8 max-w-[260px] shrink-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 pl-2.5 pr-2 h-8 text-[13px] rounded-full border border-stone-300 hover:bg-stone-100 min-w-0 transition-colors">
            <ListIcon className="w-3.5 h-3.5 text-stone-700 shrink-0" />
            <span className="font-medium text-stone-900 truncate max-w-[180px]">{displayName}</span>
            {dirty && (
              <span className="text-xs italic text-stone-500 shrink-0">(modified)</span>
            )}
            <ChevronDown className="w-4 h-4 text-stone-600 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0 rounded-sm">
          <div className="px-3 py-2.5 border-b border-stone-200">
            <div className="text-sm font-semibold text-stone-900">Switch view</div>
          </div>
          <div className="p-1 max-h-80 overflow-y-auto">
            <button
              className={cn(
                'w-full flex items-center gap-2 px-3 h-9 text-[13px] rounded-sm text-left transition-colors',
                !activeViewId ? 'bg-[#0078d4]/10' : 'hover:bg-stone-100'
              )}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              {!activeViewId ? (
                <Check className="w-4 h-4 text-[#0078d4]" />
              ) : (
                <span className="w-4 h-4" />
              )}
              <Bookmark
                className={cn(
                  'w-3.5 h-3.5',
                  !activeViewId ? 'text-[#0078d4]' : 'text-stone-400'
                )}
              />
              <span
                className={cn(
                  !activeViewId ? 'text-[#0078d4] font-medium' : 'text-stone-900'
                )}
              >
                Default view
              </span>
            </button>

            {myViews.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-0.5 text-[11px] font-semibold text-stone-500">
                  My views
                </div>
                {myViews.map((v) => (
                  <ViewRow
                    key={v.id}
                    view={v}
                    active={activeViewId === v.id}
                    canDelete
                    onSelect={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                    onDelete={() => onDelete(v.id)}
                  />
                ))}
              </>
            )}

            {sharedViews.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-0.5 text-[11px] font-semibold text-stone-500 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Shared
                </div>
                {sharedViews.map((v) => (
                  <ViewRow
                    key={v.id}
                    view={v}
                    active={activeViewId === v.id}
                    canDelete={false}
                    showOwner
                    onSelect={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                    onDelete={() => {}}
                  />
                ))}
              </>
            )}

            {views.length === 0 && (
              <div className="px-3 py-6 text-xs text-stone-500 text-center">
                No saved views yet.
              </div>
            )}
          </div>
          <div className="border-t border-stone-200 p-1">
            <button
              className="w-full flex items-center gap-2 px-3 h-9 text-[13px] rounded-sm text-[#0078d4] font-medium hover:bg-[#0078d4]/10 text-left transition-colors"
              onClick={() => {
                setOpen(false);
                setSaveDialogOpen(true);
              }}
            >
              <Save className="w-4 h-4" />
              Save current view as…
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Popover
        open={saveMenuOpen}
        onOpenChange={(o) => dirty && setSaveMenuOpen(o)}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-sm shrink-0 transition-colors text-[#0078d4] hover:bg-[#0078d4]/10',
              !dirty && 'hidden'
            )}
            title="Save view"
          >
            <Save className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1 rounded-sm">
          {activeView && isOwner && (
            <button
              className="w-full flex items-center gap-2.5 px-3 h-8 text-[13px] text-stone-900 hover:bg-stone-100 rounded-sm text-left transition-colors"
              onClick={() => {
                onUpdate(activeView.id);
                setSaveMenuOpen(false);
              }}
            >
              <Save className="w-4 h-4 text-stone-600" />
              Save changes
            </button>
          )}
          <button
            className="w-full flex items-center gap-2.5 px-3 h-8 text-[13px] text-stone-900 hover:bg-stone-100 rounded-sm text-left transition-colors"
            onClick={() => {
              setSaveMenuOpen(false);
              setSaveDialogOpen(true);
            }}
          >
            <Save className="w-4 h-4 text-stone-600" />
            Save as new view…
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Save view as</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-stone-800 mb-1.5">
                View name
              </label>
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                }}
                placeholder="My view"
                className="rounded-sm focus-visible:ring-[#0078d4]"
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox
                checked={newShared}
                onCheckedChange={(v) => setNewShared(!!v)}
              />
              <span className="flex items-center gap-1.5 text-[13px] text-stone-800">
                <Users className="w-3.5 h-3.5 text-stone-500" />
                Share with everyone
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newName.trim()}
                className="bg-[#0078d4] hover:bg-[#106ebe] text-white rounded-sm"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ViewRowProps {
  view: SavedView;
  active: boolean;
  canDelete: boolean;
  showOwner?: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function ViewRow({ view, active, canDelete, showOwner, onSelect, onDelete }: ViewRowProps) {
  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 h-9 text-[13px] rounded-sm transition-colors',
        active ? 'bg-[#0078d4]/10' : 'hover:bg-stone-100'
      )}
    >
      <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={onSelect}>
        {active ? (
          <Check className="w-4 h-4 text-[#0078d4] shrink-0" />
        ) : (
          <span className="w-4 h-4 shrink-0" />
        )}
        <Bookmark
          className={cn(
            'w-3.5 h-3.5 shrink-0',
            active ? 'text-[#0078d4]' : 'text-stone-400'
          )}
        />
        <span className="flex flex-col min-w-0 leading-tight">
          <span
            className={cn(
              'truncate',
              active ? 'text-[#0078d4] font-medium' : 'text-stone-900'
            )}
          >
            {view.name}
          </span>
          {showOwner && (
            <span className="text-[11px] italic text-stone-500 truncate">
              {view.ownerEmail || 'unknown'}
            </span>
          )}
        </span>
      </button>
      {canDelete && (
        <button
          className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 transition-opacity"
          onClick={onDelete}
          title="Delete view"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
